import { Box, Grid, Typography } from "@mui/material";
import { OLPaper } from "OLPaper";
import { getBackendErrors, getErrorMessage } from "api/errors";
import { useReward } from "api/rewards";
import { useAppSelector } from "app/hooks";
import { CustomAttributesSection } from "components/CustomAttributesSection/CustomAttributesSection";
import { FormButtons } from "components/FormControls/FormButtons";
import { Toggle } from "components/FormControls/Toggle";
import { ImageType, ImageUpload } from "components/ImageUpload/ImageUpload";
import { getCurrentName } from "components/Layout/Achievement/AchievementImportList/utils";
import { LeaveSiteGuard } from "components/Tooltips/LeaveSiteGuard";
import { surveyQuery } from "constants/general";
import { useNotifications } from "contexts/Toast/ToastProvider";
import { Form, Formik, FormikErrors, FormikHelpers } from "formik";
import { useMessage } from "hooks/intl";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useLocation, useNavigate } from "react-router-dom";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import {
  selectAvaiableTranslations,
  selectDefaultLanguageData,
} from "reduxState/translations/translationsSlice";
import {
  CouponType,
  CouponValueSetting,
  Reward,
  RewardFields,
  RewardFieldsBE,
  RewardType,
} from "types/rewards";
import { TranslationType } from "types/translations";
import {
  backendErrors,
  parseTranslationsArrayError,
} from "utilities/backendErrors";
import { getEnvVariable } from "utilities/env";
import { focusError } from "utilities/focusError";
import { links } from "utilities/links";
import { AdditionalSettings } from "./AdditionalSettings/AdditionalSettings";
import { BasicSettings } from "./BasicSettings/BasicSettings";
import { CouponConfiguration } from "./CouponConfiguration";
import { CouponSettings } from "./CouponSettings";
import { useStyles } from "./RewardForm.styles";
import { TypeOfReward } from "./TypeOfReward";
import {
  prepareUsageLimitForRequest,
  transformTranslationsToArray,
  transformTranslationsToObject,
} from "./rewardFormHelpers";

type FormValues = RewardFields & {
  areAutoGeneratedCouponCodes?: string;
  specificExpiringDate?: boolean;
};

interface Props {
  editMode?: boolean;
  rewardData?: RewardFieldsBE;
  rewardID?: string;
  initialErrors?: FormikErrors<FormValues>;
  onSaveCallback?: (reward: any) => void;
  onCancelCallback?: () => void;
}

const getInitialCouponType = (
  reward?: Reward,
  couponValueType?: "money" | "percentage",
) => {
  switch (reward) {
    case "conversion_coupon":
      return reward;
    case "dynamic_coupon":
      return "money";
    default:
      return couponValueType;
  }
};

export function RewardForm({
  editMode,
  rewardData,
  rewardID,
  initialErrors,
  onSaveCallback,
  onCancelCallback,
}: Props) {
  const [images, setImages] = useState<ImageType[]>([]);
  const classes = useStyles();
  const { message } = useMessage();
  const navigate = useNavigate();
  const { code } = useAppSelector(selectCurrentStoreData);
  const availableLanguages = useAppSelector(selectAvaiableTranslations);
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const { addReward, editReward, removePhoto, setPhoto } = useReward();
  const { notify } = useNotifications();
  const { state } = useLocation();

  const rewardDataRatio = rewardData?.unitsConversion?.ratio
    ? Number((1 / rewardData?.unitsConversion?.ratio).toFixed(3))
    : undefined;

  const [activeReward, setActiveReward] = useState<RewardType>(
    rewardData && rewardData?.reward !== "material" ? "coupon" : "material",
  );

  const [activeCouponType, setActiveCouponType] = useState<CouponType>(
    getInitialCouponType(rewardData?.reward, rewardData?.couponValueType),
  );

  const [activeCouponValueSetting, setActiveCouponValueSetting] =
    useState<CouponValueSetting>(
      rewardData &&
        rewardData?.reward !== "material" &&
        rewardData?.reward !== "conversion_coupon"
        ? rewardData?.reward
        : undefined,
    );

  const [reward, setReward] = useState<Reward | undefined>(
    rewardData?.reward ? rewardData?.reward : "material",
  );

  const [activeTranslation, setActiveTranslation] = useState(1);

  const initialValues: FormValues = rewardData
    ? {
        reward: rewardData.reward,
        couponValueType: rewardData.couponValueType,
        translations: Array.isArray(rewardData.translations)
          ? [
              ...rewardData.translations?.filter(
                (translation) => translation?.locale === defaultLang?.code,
              ),
              ...rewardData.translations?.filter(
                (translation) => translation?.locale !== defaultLang?.code,
              ),
            ]
          : transformTranslationsToArray(rewardData.translations),
        visibility: {
          allTime: rewardData.visibility?.allTime?.toString(),
          from: rewardData.visibility?.from || null,
          to: rewardData.visibility?.to || null,
        },
        activity: {
          allTime: rewardData.activity?.allTime?.toString(),
          from: rewardData.activity?.from || null,
          to: rewardData.activity?.to || null,
        },
        target: rewardData.segments?.length
          ? "segment"
          : rewardData.levels?.length
            ? "level"
            : "all",
        costInPoints: rewardData.costInPoints,
        daysInactive: rewardData.daysInactive,
        daysValid: rewardData.daysValid,
        dateValid: rewardData.dateValid,
        couponValue: rewardData.couponValue,
        active: rewardData.active,
        categories: initialErrors?.categories ? [] : rewardData.categories,
        levels: initialErrors?.levels ? [] : rewardData.levels,
        segments: initialErrors?.segments ? [] : rewardData.segments,
        couponGenerator: rewardData.couponGenerator,
        usageLimit: {
          general:
            rewardData.usageLimit?.general === -1
              ? undefined
              : rewardData.usageLimit?.general,
          perUser:
            rewardData.usageLimit?.perUser === -1
              ? undefined
              : rewardData.usageLimit?.perUser,
        },
        areAutoGeneratedCouponCodes: rewardData.couponGenerator?.length
          ? "true"
          : "false",
        specificExpiringDate: !!rewardData.dateValid,
        labels: rewardData.labels,
        unitsConversion: {
          ratio: rewardDataRatio,
          rounding: rewardData.unitsConversion?.rounding,
        },
        sourceWalletTypeCode: rewardData.sourceWalletTypeCode,
        price: rewardData.price,
      }
    : {
        reward: "material",
        labels: [],
        translations: [
          defaultLang && {
            code: defaultLang.code,
            languageName: defaultLang.name,
          },
        ],
        visibility: { allTime: "true", from: null, to: null },
        activity: { allTime: "true", from: null, to: null },
        target: "all",
        unitsConversion: { ratio: undefined, rounding: "default" },
        areAutoGeneratedCouponCodes: "false",
        dateValid: new Date().toISOString(),
      };
  const moneyOrPercentageCouponValueType =
    activeCouponType !== "conversion_coupon" &&
    activeCouponValueSetting !== "dynamic_coupon";

  useEffect(() => {
    if (activeReward === "material") {
      setReward(activeReward);
    } else if (
      activeReward === "coupon" &&
      activeCouponType === "conversion_coupon"
    ) {
      setReward(activeCouponType);
    } else if (activeReward === "coupon" && activeCouponType === "percentage") {
      setReward("static_coupon");
    } else {
      setReward(activeCouponValueSetting);
    }
  }, [activeReward, activeCouponType, activeCouponValueSetting]);

  const onSubmit = async (
    values: FormValues,
    { setSubmitting, setStatus }: FormikHelpers<FormValues>,
  ) => {
    setSubmitting(true);
    setStatus({});

    const addPriceInCurrencyInput =
      activeReward === "material" ||
      (activeCouponType === "money" &&
        activeCouponValueSetting === "static_coupon");
    const valuesRatio = values.unitsConversion?.ratio
      ? 1 / Number(values.unitsConversion.ratio.toFixed(3))
      : undefined;

    const properties = {
      ...values,
      reward: editMode ? undefined : reward,
      ...(reward === "material" && {
        daysInactive: undefined,
        daysValid: undefined,
        dateValid: undefined,
        couponValue: undefined,
        couponGenerator: undefined,
      }),
      ...(reward !== "static_coupon" && {
        couponValue: undefined,
      }),
      ...(reward !== "conversion_coupon"
        ? {
            unitsConversion: undefined,
          }
        : {
            unitsConversion: {
              ratio: valuesRatio,
              rounding: values.unitsConversion?.rounding,
            },
          }),
      ...(reward !== "material" &&
        values.areAutoGeneratedCouponCodes === "true" && {
          couponGenerator: {
            length: values.couponGenerator?.length || "",
            characterSet: values.couponGenerator?.characterSet,
            prefix: values.couponGenerator?.prefix,
          },
        }),
      ...(values.areAutoGeneratedCouponCodes === "false" && {
        couponGenerator: undefined,
      }),
      ...(values.activity?.allTime === "true"
        ? {
            activity: {
              allTime: true,
              from: undefined,
              to: undefined,
            },
          }
        : {
            activity: {
              allTime: false,
              from: values.activity?.from,
              to: values.activity?.to,
            },
          }),
      ...(values.visibility?.allTime === "true"
        ? {
            visibility: {
              allTime: true,
              from: undefined,
              to: undefined,
            },
          }
        : {
            visibility: {
              allTime: false,
              from: values.visibility?.from,
              to: values.visibility?.to,
            },
          }),
      couponValueType: moneyOrPercentageCouponValueType
        ? activeCouponType
        : undefined,
      usageLimit: prepareUsageLimitForRequest(reward, values),
      areAutoGeneratedCouponCodes: undefined,
      daysValid: values.specificExpiringDate ? undefined : values.daysValid,
      dateValid: values.specificExpiringDate ? values.dateValid : undefined,
      specificExpiringDate: undefined,
      target: values.target === "all" ? undefined : values.target,
      translations: transformTranslationsToObject(values.translations),
      photo: undefined,
      categories: values.categories,
      levels: values.levels,
      segments: values.segments,
      price: addPriceInCurrencyInput ? values.price : undefined,
    };
    try {
      const response = editMode
        ? await editReward(rewardID as string, properties)
        : await addReward(properties);

      if (editMode) {
        if (response.data) {
          await Promise.allSettled(
            images
              .filter((image) => image.status !== "saved")
              .map(async (image: ImageType) => {
                switch (image.status) {
                  case "new":
                    return await setPhoto(rewardID as string, image.file);
                  case "deleted":
                    return await removePhoto(
                      rewardID as string,
                      image.id as string,
                    );
                }
              }),
          );
        }
      } else {
        if (response?.data?.rewardId) {
          await Promise.allSettled(
            images
              .filter((image) => image.status !== "saved")
              .map(async (image: ImageType) => {
                return await setPhoto(
                  response?.data?.rewardId as string,
                  image.file,
                );
              }),
          );
        }
      }

      onSaveCallback
        ? onSaveCallback({
            ...properties,
            name: defaultLang?.code
              ? getCurrentName(
                  properties?.translations as TranslationType,
                  defaultLang.code,
                )
              : "",
            rewardId: (response as { data: { rewardId: string } }).data
              .rewardId,
          })
        : navigate(
            editMode && state?.prevPath
              ? state?.prevPath
              : editMode
                ? links.rewardsList
                : `${links.rewardsList}${surveyQuery}`,
          );

      notify(
        message({
          id: editMode ? "rewardUpdated" : "rewardAdded",
        }),
      );
    } catch (exception) {
      setSubmitting(false);
      notify(getErrorMessage(exception));
      const errors = backendErrors(getBackendErrors(exception));
      const parsedErrors = {
        ...errors,
        translations: parseTranslationsArrayError(
          errors.translations,
          values.translations,
        ),
      };
      setStatus(parsedErrors);
      focusError();
      return;
    }
  };

  useEffect(() => {
    setImages(
      rewardData?.photos
        ? rewardData.photos.map(({ id }: { id: string }) => {
            const imgUrl = `${getEnvVariable(
              "REACT_APP_API_URL",
            )}/${code}/reward/${rewardData.rewardId}/photo/${id}`;

            return {
              path: imgUrl,
              preview: imgUrl,
              status: "saved",
              id: id,
            };
          })
        : [],
    );
  }, [rewardData, code]);

  return (
    <>
      {availableLanguages && initialValues ? (
        <Formik
          initialStatus={initialErrors}
          initialValues={initialValues}
          onSubmit={onSubmit}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({
            handleSubmit,
            isSubmitting,
            status,
            values,
            dirty,
            initialValues,
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                {status?.[""] && (
                  <span className={`${classes.error} error-anchor`}>
                    {status[""]}
                  </span>
                )}
                <div className={classes.sectionHeader}>
                  {message({ id: "typeOfReward" })}
                </div>
                <TypeOfReward
                  editMode={editMode}
                  activeReward={activeReward}
                  setActiveReward={setActiveReward}
                  activeCouponType={activeCouponType}
                  setActiveCouponType={setActiveCouponType}
                  activeCouponValueSetting={activeCouponValueSetting}
                  setActiveCouponValueSetting={setActiveCouponValueSetting}
                />
                <div className={classes.sectionHeader}>
                  {message({ id: "basicSettings" })}
                </div>
                <BasicSettings
                  isEditMode={editMode}
                  activeReward={activeReward}
                  rewardData={rewardData}
                  availableLanguages={availableLanguages}
                  activeTranslation={activeTranslation}
                  setActiveTranslation={setActiveTranslation}
                />
                <div className={classes.sectionHeader}>
                  {message({ id: "additionalSettings" })}
                </div>
                <AdditionalSettings
                  availableLanguages={availableLanguages}
                  activeTranslation={activeTranslation}
                  setActiveTranslation={setActiveTranslation}
                  isEditMode={editMode}
                />
                <CustomAttributesSection labels={values.labels || []} />
                <div className={classes.sectionHeader}>
                  {message({ id: "rewardImages" })}
                  <OLPaper className={classes.OLPaper}>
                    <ImageUpload setImages={setImages} images={images} />
                  </OLPaper>
                </div>
                {activeReward !== "material" && (
                  <>
                    <div className={classes.sectionHeader}>
                      {message({ id: "couponConfiguration" })}
                    </div>
                    <CouponConfiguration />

                    <div className={classes.sectionHeader}>
                      {message({
                        id: "couponPendingAndExpirationSettings",
                      })}
                    </div>
                    <CouponSettings formikValues={values} />
                  </>
                )}
                <div className={classes.sectionHeader}>
                  {message({ id: "rewardStatus" })}
                </div>
                <OLPaper className={classes.OLPaper}>
                  <Grid container>
                    <Grid item xs={12}>
                      <Toggle
                        label={message({ id: "active" })}
                        name="active"
                        data-test-id="reward-status"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography className={classes.inputInfo}>
                        {message({ id: "rewardStatusInfo" })}
                      </Typography>
                    </Grid>
                  </Grid>
                </OLPaper>
                <Box mt={12} />
                <FormButtons
                  disableSubmit={isSubmitting}
                  onCancelClick={() => {
                    if (onCancelCallback) {
                      return onCancelCallback();
                    }
                    navigate(links.rewardsList);
                  }}
                  onSubmitClick={() => handleSubmit()}
                  {...(!editMode && { submitMessage: "addReward" })}
                />
                {!isSubmitting && (
                  <LeaveSiteGuard
                    warnBeforeRefresh
                    isDirty={dirty}
                    initialValues={initialValues}
                    currentValues={values}
                  />
                )}
              </Form>
            );
          }}
        </Formik>
      ) : (
        <>
          <Skeleton height={300} width={"100%"} />
          <Box mt={12} />
          <FormButtons
            disableSubmit
            onCancelClick={() => {}}
            onSubmitClick={() => {}}
            {...(!editMode && { submitMessage: "addReward" })}
          />
        </>
      )}
    </>
  );
}
